import { createWorkflowRuntimeState } from './workflow-runtime-state'
import { isForwardNavigationBlockedByInvalidStep } from './workflow-navigation'
import WorkflowRunnerError from './workflow-runner-error'

const wait = milliseconds => (milliseconds > 0
    ? new Promise(resolve => setTimeout(resolve, milliseconds))
    : Promise.resolve())

const isValidationResult = result => (
    result
    && typeof result.isValid === 'boolean'
    && Array.isArray(result.errors)
)

const createValidationResult = (isValid, errors = []) => ({ isValid, errors })

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)

const createWorkflowRunner = ({
    compiledFlow,
    context = {},
    completedStepIds = [],
    completedLegacyPersistedSteps = [],
    progressRepository,
}) => {
    if (!compiledFlow || !progressRepository || typeof progressRepository.markCompleted !== 'function') {
        throw new WorkflowRunnerError(
            'INVALID_RUNNER_DEPENDENCIES',
            'Workflow Runner requires a compiled flow and progressRepository.markCompleted()',
        )
    }

    if (!isObject(context)) {
        throw new WorkflowRunnerError(
            'INVALID_WORKFLOW_CONTEXT',
            'Workflow Runner context must be an object',
            { context },
        )
    }

    const controllerEntries = new Map()
    const listeners = new Set()
    const completedIds = new Set(completedStepIds)
    const completedPersistedSteps = new Set(completedLegacyPersistedSteps)
    const dirtyIds = new Set()
    const staleIds = new Set()
    const busyIds = new Set()
    const loadingIds = new Set()
    const savingIds = new Set()
    const controllerLoadingIds = new Set()
    const controllerSavingIds = new Set()
    const completionPromises = new Map()
    const backgroundProgressPersistencePromises = new Map()
    const validityByStepId = new Map()
    const errorsByStepId = new Map()
    const currentContext = context
    let openIds
    let state
    let terminalExecutionPromise = null
    let initializationPromise = null
    let lifecyclePromise = Promise.resolve()

    const rebuildState = () => {
        state = createWorkflowRuntimeState(compiledFlow, {
            context: currentContext,
            completedStepIds: Array.from(completedIds),
            completedLegacyPersistedSteps: Array.from(completedPersistedSteps),
            openStepIds: openIds,
            validityByStepId,
            dirtyStepIds: Array.from(dirtyIds),
            staleStepIds: Array.from(staleIds),
            busyStepIds: Array.from(busyIds),
            loadingStepIds: Array.from(new Set([...loadingIds, ...controllerLoadingIds])),
            savingStepIds: Array.from(new Set([...savingIds, ...controllerSavingIds])),
            errorsByStepId,
        })
        openIds = state.steps.filter(step => step.isOpen).map(step => step.id)
    }

    const notify = () => listeners.forEach(listener => listener(state))

    const rebuildAndNotify = () => {
        rebuildState()
        notify()
    }

    const getStepState = stepId => {
        const stepState = state.getStepState(stepId)

        if (!stepState) {
            throw new WorkflowRunnerError(
                'UNKNOWN_STEP_INSTANCE',
                `Step instance "${stepId}" is not part of the workflow`,
                { stepId },
            )
        }

        return stepState
    }

    const setStepError = (stepId, error) => {
        if (error === null) {
            errorsByStepId.delete(stepId)
        } else {
            errorsByStepId.set(stepId, error)
        }
    }

    const reportStepError = (stepId, error) => {
        getStepState(stepId)
        setStepError(stepId, error)
        rebuildAndNotify()
    }

    const setStepValidity = (stepId, validation) => {
        getStepState(stepId)

        if (!isValidationResult(validation)) {
            throw new WorkflowRunnerError(
                'INVALID_STEP_VALIDATION_RESULT',
                `Step "${stepId}" must provide { isValid, errors } validation`,
                { stepId, validation },
            )
        }

        validityByStepId.set(
            stepId,
            createValidationResult(validation.isValid, [...validation.errors]),
        )

        const controllerEntry = controllerEntries.get(stepId)
        const controller = controllerEntry ? controllerEntry.controller : null

        if (controller && typeof controller.setValidation === 'function') {
            controller.setValidation(validation)
        }

        rebuildAndNotify()
    }

    const setStepDirty = (stepId, isDirty = true) => {
        getStepState(stepId)

        if (isDirty) {
            dirtyIds.add(stepId)
        } else {
            dirtyIds.delete(stepId)
        }

        rebuildAndNotify()
    }

    const setStepOperationState = (stepId, operationIds, isActive) => {
        getStepState(stepId)

        if (isActive) {
            operationIds.add(stepId)
        } else {
            operationIds.delete(stepId)
        }

        rebuildAndNotify()
    }

    const setStepLoading = (stepId, isLoading = true) => {
        setStepOperationState(stepId, controllerLoadingIds, isLoading)
    }

    const setStepSaving = (stepId, isSaving = true) => {
        setStepOperationState(stepId, controllerSavingIds, isSaving)
    }

    const getControllerEntry = stepId => controllerEntries.get(stepId) || null

    const getController = stepId => {
        const entry = getControllerEntry(stepId)

        return entry ? entry.controller : null
    }

    let publishWorkflowEffect

    const assertController = (stepId, controller) => {
        if (!controller || typeof controller.flush !== 'function') {
            throw new WorkflowRunnerError(
                'INVALID_STEP_CONTROLLER',
                `Step "${stepId}" controller requires flush()`,
                { stepId },
            )
        }
    }

    const synchronizeControllerValidation = (stepId, controller) => {
        const validation = validityByStepId.get(stepId)

        if (validation && typeof controller.setValidation === 'function') {
            controller.setValidation(validation)
        }
    }

    const createControllerEntry = stepState => {
        const controller = stepState.createController({
            flow: compiledFlow,
            step: compiledFlow.getStep(stepState.id),
            context: currentContext,
            getContext: () => currentContext,
            getRuntimeState: () => getStepState(stepState.id),
            setValidity: validation => setStepValidity(stepState.id, validation),
            setDirty: isDirty => setStepDirty(stepState.id, isDirty),
            setLoading: isLoading => setStepLoading(stepState.id, isLoading),
            setSaving: isSaving => setStepSaving(stepState.id, isSaving),
            reportError: error => reportStepError(stepState.id, error),
            completeExternalStep: validation => runner.completeExternalStep(stepState.id, validation),
            publishEffect: effect => publishWorkflowEffect({
                ...effect,
                sourceStepId: stepState.id,
            }),
        })

        assertController(stepState.id, controller)
        synchronizeControllerValidation(stepState.id, controller)

        const entry = {
            controller,
            loaded: false,
            active: false,
        }

        controllerEntries.set(stepState.id, entry)

        return entry
    }

    const ensureControllerEntry = stepState => {
        const existingEntry = getControllerEntry(stepState.id)

        if (existingEntry) {
            return existingEntry
        }

        try {
            return createControllerEntry(stepState)
        } catch (error) {
            setStepError(stepState.id, error)
            rebuildAndNotify()
            throw error
        }
    }

    const loadController = async (stepState, entry) => {
        if (entry.loaded && !stepState.isStale) {
            return
        }

        loadingIds.add(stepState.id)
        rebuildAndNotify()

        try {
            if (typeof entry.controller.load === 'function') {
                await entry.controller.load()
            }

            Object.assign(entry, { loaded: true })
            staleIds.delete(stepState.id)
            setStepError(stepState.id, null)
        } catch (error) {
            Object.assign(entry, { loaded: false })
            setStepError(stepState.id, error)
            throw error
        } finally {
            loadingIds.delete(stepState.id)
            rebuildAndNotify()
        }
    }

    const activateController = async (stepState, entry) => {
        if (entry.active || !stepState.isOpen) {
            return
        }

        try {
            if (typeof entry.controller.activate === 'function') {
                await entry.controller.activate()
            }

            Object.assign(entry, { active: true })
            setStepError(stepState.id, null)
        } catch (error) {
            setStepError(stepState.id, error)
            rebuildAndNotify()
            throw error
        }
    }

    const deactivateController = async (stepId, entry) => {
        if (!entry.active) {
            return
        }

        try {
            if (typeof entry.controller.deactivate === 'function') {
                await entry.controller.deactivate()
            }

            Object.assign(entry, { active: false })
        } catch (error) {
            setStepError(stepId, error)
            rebuildAndNotify()
            throw error
        }
    }

    const disposeController = async (stepId, entry) => {
        await deactivateController(stepId, entry)

        if (typeof entry.controller.dispose === 'function') {
            await entry.controller.dispose()
        }

        controllerEntries.delete(stepId)
        controllerLoadingIds.delete(stepId)
        controllerSavingIds.delete(stepId)
    }

    const reconcileControllers = async () => {
        const mountedStepIds = new Set(
            state.steps.filter(step => step.isMounted).map(step => step.id),
        )

        const existingEntries = Array.from(controllerEntries.entries())

        for (let index = 0; index < existingEntries.length; index += 1) {
            const [stepId, entry] = existingEntries[index]

            if (!mountedStepIds.has(stepId)) {
                // Lifecycle order is deterministic and follows the compiled flow where applicable.
                // eslint-disable-next-line no-await-in-loop
                await disposeController(stepId, entry)
            }
        }

        for (let index = 0; index < state.steps.length; index += 1) {
            const stepState = state.steps[index]

            if (stepState.isMounted) {
                const entry = ensureControllerEntry(stepState)

                // Lifecycle order is deterministic and follows the compiled flow.
                // eslint-disable-next-line no-await-in-loop
                await loadController(stepState, entry)

                if (stepState.isOpen) {
                    // eslint-disable-next-line no-await-in-loop
                    await activateController(stepState, entry)
                } else {
                    // eslint-disable-next-line no-await-in-loop
                    await deactivateController(stepState.id, entry)
                }
            }
        }
    }

    const scheduleLifecycleReconciliation = () => {
        const operation = lifecyclePromise
            .catch(() => undefined)
            .then(() => reconcileControllers())

        lifecyclePromise = operation

        return operation
    }

    const updateContext = nextContext => {
        if (!isObject(nextContext)) {
            throw new WorkflowRunnerError(
                'INVALID_WORKFLOW_CONTEXT',
                'Workflow context update must be an object',
                { context: nextContext },
            )
        }

        if (nextContext !== currentContext) {
            Object.keys(currentContext).forEach(key => {
                delete currentContext[key]
            })
            Object.assign(currentContext, nextContext)
        }

        rebuildAndNotify()

        return scheduleLifecycleReconciliation()
    }

    const validateStep = async stepState => {
        if (stepState.validation.mode === 'none') {
            return createValidationResult(true)
        }

        const controller = getController(stepState.id)

        if (!controller) {
            throw new WorkflowRunnerError(
                'STEP_CONTROLLER_NOT_REGISTERED',
                `Step "${stepState.id}" cannot be completed before its controller is registered`,
                { stepId: stepState.id },
            )
        }

        if (typeof controller.validate !== 'function') {
            throw new WorkflowRunnerError(
                'STEP_VALIDATOR_NOT_AVAILABLE',
                `Step "${stepState.id}" uses component validation but its controller has no validate()`,
                { stepId: stepState.id },
            )
        }

        const validation = await controller.validate()

        if (!isValidationResult(validation)) {
            throw new WorkflowRunnerError(
                'INVALID_STEP_VALIDATION_RESULT',
                `Step controller "${stepState.id}" must return { isValid, errors }`,
                { stepId: stepState.id, validation },
            )
        }

        return validation
    }

    const flushStep = async stepState => {
        const controller = getController(stepState.id)

        if (!controller) {
            return
        }

        savingIds.add(stepState.id)
        rebuildAndNotify()

        try {
            await controller.flush()
            dirtyIds.delete(stepState.id)
        } finally {
            savingIds.delete(stepState.id)
            rebuildAndNotify()
        }
    }

    const refreshWorkflowAvailability = async () => {
        const services = currentContext.services || {}

        if (typeof services.refreshWorkflowAvailability === 'function') {
            await services.refreshWorkflowAvailability()
        }
    }

    const findNextStep = stepId => {
        const currentStepIndex = state.steps.findIndex(step => step.id === stepId)

        return state.steps
            .slice(currentStepIndex + 1)
            .find(step => step.isReachable) || null
    }

    const openNextStep = stepId => {
        const nextStep = findNextStep(stepId)

        if (!nextStep) {
            return null
        }

        if (compiledFlow.settings.accordion) {
            openIds = [nextStep.id]
        } else {
            openIds = Array.from(new Set([...openIds, nextStep.id]))
        }

        return nextStep.id
    }

    const markStepCompletedLocally = stepState => {
        completedIds.add(stepState.id)
        if (stepState.legacy) {
            completedPersistedSteps.add(stepState.legacy.persistedStep)
        }
        setStepError(stepState.id, null)
    }

    const rollbackBackgroundProgress = (stepState, error) => {
        completedIds.delete(stepState.id)
        if (stepState.legacy) {
            completedPersistedSteps.delete(stepState.legacy.persistedStep)
        }

        if (compiledFlow.settings.accordion) {
            openIds = [stepState.id]
        } else if (!openIds.includes(stepState.id)) {
            openIds = [...openIds, stepState.id]
        }

        setStepError(stepState.id, error)
        rebuildAndNotify()
    }

    const persistProgress = stepState => progressRepository.markCompleted({
        flowId: compiledFlow.id,
        flowVersion: compiledFlow.version,
        stepId: stepState.id,
        legacyPersistedStep: stepState.legacy && stepState.legacy.persistedStep,
    })

    const persistProgressInBackground = stepState => {
        const persistencePromise = Promise.resolve()
            .then(() => persistProgress(stepState))
            .catch(error => {
                rollbackBackgroundProgress(stepState, error)
            })
            .finally(() => {
                backgroundProgressPersistencePromises.delete(stepState.id)
            })

        backgroundProgressPersistencePromises.set(stepState.id, persistencePromise)
    }

    const complete = async (
        stepId,
        expectedCompletionMode,
        externalValidation = null,
        wasReachableAtRequest = false,
    ) => {
        let stepState = getStepState(stepId)
        const canCompleteConfirmedExternalStep = expectedCompletionMode === 'external'
            && wasReachableAtRequest

        if (!stepState.isReachable && !canCompleteConfirmedExternalStep) {
            return { completed: false, reason: 'unavailable' }
        }

        if (stepState.completion.mode !== expectedCompletionMode) {
            throw new WorkflowRunnerError(
                'UNSUPPORTED_COMPLETION_REQUEST',
                `Step "${stepId}" does not support ${expectedCompletionMode} completion`,
                { stepId, expectedCompletionMode },
            )
        }
        try {
            await scheduleLifecycleReconciliation()
            stepState = getStepState(stepId)
        } catch (error) {
            return { completed: false, reason: 'load-error', error }
        }

        let validation

        try {
            validation = externalValidation || await validateStep(stepState)
            setStepValidity(stepId, validation)
        } catch (error) {
            setStepError(stepId, error)
            rebuildAndNotify()

            return { completed: false, reason: 'validation-error', error }
        }

        if (!validation.isValid) {
            return { completed: false, reason: 'invalid', validation }
        }

        try {
            await flushStep(stepState)
        } catch (error) {
            setStepError(stepId, error)
            rebuildAndNotify()

            return { completed: false, reason: 'flush-error', error }
        }

        try {
            await refreshWorkflowAvailability()
        } catch (error) {
            setStepError(stepId, error)
            rebuildAndNotify()

            return { completed: false, reason: 'availability-refresh-error', error }
        }

        if (!stepState.completion.persistProgress) {
            markStepCompletedLocally(stepState)
        } else if (compiledFlow.settings.progressPersistenceMode === 'background') {
            markStepCompletedLocally(stepState)
            persistProgressInBackground(stepState)
        } else {
            try {
                await persistProgress(stepState)
            } catch (error) {
                setStepError(stepId, error)
                rebuildAndNotify()

                return { completed: false, reason: 'progress-error', error }
            }

            markStepCompletedLocally(stepState)
        }
        rebuildState()
        rebuildAndNotify()
        await wait(compiledFlow.settings.nextStepOpenDelayMs)
        const nextStepId = openNextStep(stepId)
        rebuildAndNotify()

        try {
            await scheduleLifecycleReconciliation()
        } catch (error) {
            return {
                completed: true,
                nextStepId,
                lifecycleError: error,
            }
        }

        return { completed: true, nextStepId }
    }

    const runStepCompletion = (stepId, completionMode, externalValidation = null) => {
        if (terminalExecutionPromise) {
            return Promise.resolve({ completed: false, reason: 'busy' })
        }

        if (completionPromises.has(stepId)) {
            return completionPromises.get(stepId)
        }

        if (completionPromises.size > 0) {
            return Promise.resolve({ completed: false, reason: 'busy' })
        }

        busyIds.add(stepId)
        const wasReachableAtRequest = getStepState(stepId).isReachable

        const completionPromise = Promise.resolve()
            .then(() => complete(
                stepId,
                completionMode,
                externalValidation,
                wasReachableAtRequest,
            ))
            .finally(() => {
                completionPromises.delete(stepId)
                busyIds.delete(stepId)
                rebuildAndNotify()
            })

        completionPromises.set(stepId, completionPromise)
        rebuildAndNotify()

        return completionPromise
    }

    const validateForTerminal = async terminalStepId => {
        const terminalStep = getStepState(terminalStepId)
        const validationScope = terminalStep.completion.validationScope || 'all'
        const activeSteps = state.steps.filter(step => (
            step.id !== terminalStepId
            && step.isVisible
            && !step.isDisabled
            && step.completion.mode !== 'terminal'
            && validationScope !== 'none'
            && (
                validationScope === 'all'
                || step.completion.requiredForTerminal === true
            )
        ))

        for (let index = 0; index < activeSteps.length; index += 1) {
            const stepState = activeSteps[index]

            if (stepState.validation.mode === 'external') {
                if (!stepState.isCompleted) {
                    const validation = createValidationResult(false, [{
                        code: 'EXTERNAL_STEP_NOT_COMPLETED',
                        stepId: stepState.id,
                    }])

                    setStepValidity(stepState.id, validation)

                    return { isValid: false, stepId: stepState.id, validation }
                }

                setStepValidity(stepState.id, createValidationResult(true))
            } else {
                try {
                    // Validation order must follow flow order so the first failing step is deterministic.
                    // eslint-disable-next-line no-await-in-loop
                    const validation = await validateStep(stepState)

                    setStepValidity(stepState.id, validation)

                    if (!validation.isValid) {
                        return { isValid: false, stepId: stepState.id, validation }
                    }
                } catch (error) {
                    setStepError(stepState.id, error)
                    rebuildAndNotify()

                    return { isValid: false, stepId: stepState.id, error }
                }
            }
        }

        return { isValid: true }
    }

    const flushDirtyMountedSteps = async () => {
        const dirtyMountedSteps = state.steps.filter(step => step.isDirty && step.isMounted)

        for (let index = 0; index < dirtyMountedSteps.length; index += 1) {
            const stepState = dirtyMountedSteps[index]
            const controller = getController(stepState.id)

            if (!controller) {
                throw new WorkflowRunnerError(
                    'STEP_CONTROLLER_NOT_REGISTERED',
                    `Dirty step "${stepState.id}" cannot be flushed before its controller is registered`,
                    { stepId: stepState.id },
                )
            }

            // Flushes share domain scopes and must not run concurrently.
            // eslint-disable-next-line no-await-in-loop
            await flushStep(stepState)
        }
    }

    rebuildState()

    const runner = Object.freeze({
        getState() {
            return state
        },
        subscribe(listener) {
            listeners.add(listener)
            listener(state)

            return () => listeners.delete(listener)
        },
        initialize() {
            if (!initializationPromise) {
                initializationPromise = scheduleLifecycleReconciliation()
                    .catch(error => {
                        initializationPromise = null
                        throw error
                    })
            }

            return initializationPromise
        },
        async registerController(stepId, controller) {
            const stepState = getStepState(stepId)

            assertController(stepId, controller)

            const previousEntry = getControllerEntry(stepId)

            if (previousEntry && previousEntry.controller !== controller) {
                await disposeController(stepId, previousEntry)
            }

            synchronizeControllerValidation(stepId, controller)

            controllerEntries.set(stepId, {
                controller,
                loaded: typeof controller.load !== 'function',
                active: false,
            })

            if (stepState.isMounted) {
                return scheduleLifecycleReconciliation()
            }

            return Promise.resolve()
        },
        async unregisterController(stepId) {
            const entry = getControllerEntry(stepId)

            if (!entry) {
                return
            }

            await disposeController(stepId, entry)
            rebuildAndNotify()
        },
        async dispose() {
            await lifecyclePromise.catch(() => undefined)

            const existingEntries = Array.from(controllerEntries.entries())

            for (let index = 0; index < existingEntries.length; index += 1) {
                const [stepId, entry] = existingEntries[index]

                // Controllers are disposed in their deterministic insertion order.
                // eslint-disable-next-line no-await-in-loop
                await disposeController(stepId, entry)
            }

            listeners.clear()
        },
        async openStep(stepId) {
            const stepState = getStepState(stepId)

            if (
                !stepState.isReachable
                || isForwardNavigationBlockedByInvalidStep({
                    steps: state.steps,
                    targetStepId: stepId,
                    blockForwardOnInvalidSteps: compiledFlow.settings.navigation
                        .blockForwardOnInvalidSteps,
                })
            ) {
                return false
            }

            openIds = compiledFlow.settings.accordion
                ? [stepId]
                : Array.from(new Set([...openIds, stepId]))
            rebuildAndNotify()

            await scheduleLifecycleReconciliation()

            return true
        },
        async closeStep(stepId) {
            getStepState(stepId)
            openIds = openIds.filter(openStepId => openStepId !== stepId)
            rebuildAndNotify()

            await scheduleLifecycleReconciliation()
        },
        updateContext,
        replaceContext: updateContext,
        setStepValidity,
        setStepDirty,
        setStepLoading,
        setStepSaving,
        reportStepError,
        async scheduleStepSave(stepId) {
            const stepState = getStepState(stepId)

            await scheduleLifecycleReconciliation()

            const controller = getController(stepId)

            if (!controller || typeof controller.scheduleSave !== 'function') {
                throw new WorkflowRunnerError(
                    'STEP_SAVE_NOT_SUPPORTED',
                    `Step "${stepId}" does not support scheduled saves`,
                    { stepId },
                )
            }

            return controller.scheduleSave({
                flow: compiledFlow,
                step: stepState,
            })
        },
        async dispatchStepAction(stepId, action) {
            getStepState(stepId)

            if (!isObject(action) || typeof action.type !== 'string' || action.type.length === 0) {
                throw new WorkflowRunnerError(
                    'INVALID_STEP_ACTION',
                    `Step "${stepId}" action requires a non-empty type`,
                    { stepId, action },
                )
            }

            await scheduleLifecycleReconciliation()

            const controller = getController(stepId)

            if (!controller || typeof controller.handleAction !== 'function') {
                throw new WorkflowRunnerError(
                    'STEP_ACTION_NOT_SUPPORTED',
                    `Step "${stepId}" does not support actions`,
                    { stepId, action },
                )
            }

            try {
                return await controller.handleAction(action)
            } catch (error) {
                setStepError(stepId, error)
                rebuildAndNotify()
                throw error
            }
        },
        clearStepStale(stepId) {
            getStepState(stepId)
            staleIds.delete(stepId)
            rebuildAndNotify()
        },
        async publishEffect({
            type,
            phase,
            sourceStepId,
            scopes,
            payload = null,
        }) {
            const sourceStep = getStepState(sourceStepId)

            if (typeof type !== 'string' || type.length === 0) {
                throw new WorkflowRunnerError(
                    'INVALID_WORKFLOW_EFFECT_TYPE',
                    'Workflow effect requires a type',
                    { type },
                )
            }

            if (phase !== 'draft' && phase !== 'committed') {
                throw new WorkflowRunnerError(
                    'INVALID_WORKFLOW_EFFECT_PHASE',
                    'Workflow effect phase must be draft or committed',
                    { phase },
                )
            }

            if (!Array.isArray(scopes) || scopes.some(scope => typeof scope !== 'string' || scope.length === 0)) {
                throw new WorkflowRunnerError(
                    'INVALID_WORKFLOW_EFFECT_SCOPES',
                    'Workflow effect scopes must be an array of non-empty strings',
                    { scopes },
                )
            }

            if (!sourceStep.descriptor.effects.publishes.includes(type)) {
                throw new WorkflowRunnerError(
                    'UNDECLARED_WORKFLOW_EFFECT',
                    `Step "${sourceStepId}" cannot publish effect "${type}"`,
                    { sourceStepId, type },
                )
            }

            if (scopes.some(scope => !sourceStep.descriptor.dataScopes.writes.includes(scope))) {
                throw new WorkflowRunnerError(
                    'UNDECLARED_WORKFLOW_EFFECT_SCOPE',
                    `Step "${sourceStepId}" cannot publish effect outside its write scopes`,
                    { sourceStepId, scopes },
                )
            }

            const effect = Object.freeze({
                type,
                phase,
                sourceStepId,
                scopes: Object.freeze([...scopes]),
                payload,
            })

            rebuildAndNotify()
            scheduleLifecycleReconciliation().catch(() => undefined)

            const deliveredStepIds = []
            const staleStepIds = []
            const failedStepIds = []
            const observers = state.steps.filter(step => (
                step.id !== sourceStepId
                && step.descriptor.effects.observes.includes(type)
            ))

            for (let index = 0; index < observers.length; index += 1) {
                const observer = observers[index]
                const controller = getController(observer.id)

                if (!observer.isMounted || !controller || typeof controller.handleEffect !== 'function') {
                    staleIds.add(observer.id)
                    staleStepIds.push(observer.id)
                } else {
                    try {
                        // Effect delivery follows flow order to keep refreshes deterministic.
                        // eslint-disable-next-line no-await-in-loop
                        await controller.handleEffect(effect)
                        staleIds.delete(observer.id)
                        deliveredStepIds.push(observer.id)
                    } catch (error) {
                        staleIds.add(observer.id)
                        setStepError(observer.id, error)
                        failedStepIds.push(observer.id)
                    }
                }
            }

            rebuildAndNotify()

            return {
                effect,
                deliveredStepIds,
                staleStepIds,
                failedStepIds,
            }
        },
        completeStep(stepId) {
            return runStepCompletion(stepId, 'button')
        },
        completeExternalStep(stepId, validation = createValidationResult(true)) {
            if (!isValidationResult(validation)) {
                throw new WorkflowRunnerError(
                    'INVALID_STEP_VALIDATION_RESULT',
                    `Step "${stepId}" must provide { isValid, errors } validation`,
                    { stepId, validation },
                )
            }

            return runStepCompletion(stepId, 'external', validation)
        },
        async executeTerminalStep(stepId, terminalAction) {
            if (terminalExecutionPromise) {
                return terminalExecutionPromise
            }

            if (completionPromises.size > 0) {
                return { executed: false, reason: 'busy' }
            }

            busyIds.add(stepId)

            const operation = Promise.resolve().then(async () => {
                let terminalStep = getStepState(stepId)

                if (!terminalStep.isReachable) {
                    return { executed: false, reason: 'unavailable' }
                }

                if (terminalStep.completion.mode !== 'terminal') {
                    throw new WorkflowRunnerError(
                        'UNSUPPORTED_COMPLETION_REQUEST',
                        `Step "${stepId}" is not a terminal step`,
                        { stepId },
                    )
                }

                if (typeof terminalAction !== 'function') {
                    throw new WorkflowRunnerError(
                        'INVALID_TERMINAL_ACTION',
                        'Terminal completion requires an action function',
                        { stepId },
                    )
                }
                try {
                    await scheduleLifecycleReconciliation()
                    terminalStep = getStepState(stepId)
                } catch (error) {
                    return { executed: false, reason: 'load-error', error }
                }

                const terminalValidation = await validateForTerminal(stepId)

                if (!terminalValidation.isValid) {
                    return {
                        executed: false,
                        reason: 'invalid',
                        ...terminalValidation,
                    }
                }

                try {
                    await flushDirtyMountedSteps()
                } catch (error) {
                    setStepError(stepId, error)
                    rebuildAndNotify()

                    return { executed: false, reason: 'flush-error', error }
                }

                try {
                    const result = await terminalAction({
                        flow: compiledFlow,
                        state,
                        step: terminalStep,
                    })

                    completedIds.add(stepId)
                    setStepError(stepId, null)
                    rebuildAndNotify()

                    return { executed: true, result }
                } catch (error) {
                    setStepError(stepId, error)
                    rebuildAndNotify()

                    return { executed: false, reason: 'terminal-error', error }
                }
            })

            terminalExecutionPromise = operation.finally(() => {
                terminalExecutionPromise = null
                busyIds.delete(stepId)
                rebuildAndNotify()
            })
            rebuildAndNotify()

            return terminalExecutionPromise
        },
    })

    publishWorkflowEffect = effect => runner.publishEffect(effect)

    return runner
}

export { createWorkflowRunner }
export default createWorkflowRunner
